import React from "react";
import styles from "./AttachmentPreviewList.module.scss";

/**
 * Reusable attachment preview list used in chat + modals.
 *
 * Expected item shape:
 * {
 *   previewUrl?: string | null,
 *   url?: string | null,
 *   progress?: number,
 *   uploading?: boolean,
 *   file?: File
 * }
 */

const isVideoUrl = (url = "") => /\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)(\?.*)?$/i.test(url);
const isVideoFile = (file) => file && file.type && file.type.startsWith('video/');

const AttachmentPreviewList = ({
    items = [],
    onRemove,
    className = "",
    showProgress = true,
}) => {
    if (!items || items.length === 0) return null;

    return (
        <div className={`${styles.previewList} ${className || ""}`}>
            {items.map((item, index) => {
                const src = item?.previewUrl || item?.url;
                if (!src) return null;
                const progress = typeof item?.progress === "number" ? Math.max(0, Math.min(100, item.progress)) : null;
                const isUploading = !!item?.uploading;
                const isVideo = isVideoUrl(src) || isVideoFile(item?.file);

                return (
                    <div key={index} className={styles.previewItem}>
                        {isVideo ? (
                            <div className={styles.videoPreview}>
                                <video
                                    src={src}
                                    className={styles.previewVideo}
                                    muted
                                />
                                <div className={styles.videoPlayIcon}>▶</div>
                            </div>
                        ) : (
                            <img
                                src={src}
                                alt="attachment-preview"
                                className={styles.previewImage}
                            />
                        )}
                        {showProgress && isUploading && progress !== null && (
                            <div className={styles.progressText}>
                                {progress}%
                            </div>
                        )}
                        {showProgress && progress !== null && (
                            <div className={styles.progressBar}>
                                <div
                                    className={styles.progressFill}
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        )}
                        {onRemove && (
                            <button
                                type="button"
                                className={styles.removeButton}
                                onClick={() => onRemove(index)}
                                aria-label="Remove attachment"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default AttachmentPreviewList;
